// What survives the visitor moving to another page of the site: the frame is
// reloaded with every page, so the conversation lives in sessionStorage
// (per tab, partitioned per client site, gone when the tab closes).
// Consent is remembered longer, in localStorage.

import type { Message } from "./types.ts";

const MAX_MESSAGES = 40;
const CONSENT_DAYS = 180;

export type LeadState = "hidden" | "offered" | "open" | "sent" | "dismissed";

/** Filled into the contact form: what the visitor already typed in the chat. */
export interface LeadPrefill {
  message?: string;
  phone?: string;
  email?: string;
}

export interface Saved {
  sessionId: string;
  conversationId: string | null;
  messages: Message[];
  lead: LeadState;
  leadPrefill?: LeadPrefill;
}

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

export function sessionStore(key: string) {
  const name = `unitivo:${key.slice(-12)}`;
  const consentName = `${name}:consent`;

  return {
    load(): Saved {
      const saved = safe(() => JSON.parse(sessionStorage.getItem(name) ?? "null") as Saved | null, null);
      if (saved?.sessionId) {
        // A reply cut off by navigation is kept as what arrived, not "streaming".
        return { ...saved, messages: saved.messages.map((m) => (m.status === "streaming" ? { ...m, status: undefined } : m)) };
      }
      return { sessionId: newSessionId(), conversationId: null, messages: [], lead: "hidden" };
    },
    save(state: Saved) {
      const trimmed = { ...state, messages: state.messages.slice(-MAX_MESSAGES) };
      safe(() => sessionStorage.setItem(name, JSON.stringify(trimmed)), undefined);
    },
    clear() {
      safe(() => sessionStorage.removeItem(name), undefined);
    },
    consentGiven(): boolean {
      const at = safe(() => Number(localStorage.getItem(consentName)), 0);
      return at > 0 && Date.now() - at < CONSENT_DAYS * 86_400_000;
    },
    giveConsent() {
      safe(() => localStorage.setItem(consentName, String(Date.now())), undefined);
    },
  };
}

export function newSessionId(): string {
  return `w-${crypto.randomUUID()}`;
}
